import {
    BasisOfQuotations,
    isMarketOpenState,
    OptionQuoteRecord,
    PriceBasis,
    QuoteRecord,
    QuoteStatus,
    TmcQuoteRecord,
    TradingState,
    UnderlyingQuoteRecord,
    WarrantQuoteRecord
} from "./QuoteRecord";
import {IsoDateTime, OffsetDateTime} from "../../pseudo-types/DateTypes";
import {quoteHashCode} from "../util/QuoteStoreUtil";
import {QuoteChangeDirection} from "./QuoteChangeDirection";
import {Exchange} from "../../instruments/exchange/Exchange";
import {PriceStrategy} from "../../components/inputs/price/input/PriceStrategy";
import {PortfolioType} from "../../portfolio/models/Portfolio";

export type QuoteViewModels = ReadonlyArray<QuoteViewModel>;

export abstract class QuoteViewModel<T extends QuoteRecord = QuoteRecord> implements QuoteRecord {

    public readonly hashCode: string;

    protected constructor(public readonly quote: T,
                          public readonly previousQuote: T | null,
                          public readonly strategy: PriceStrategy) {
        this.hashCode = quoteHashCode(quote);
    }

    public abstract clone(): QuoteViewModel<T>;

    public get code(): string {
        return this.quote.code;
    }

    public get exchange(): Exchange {
        return this.quote.exchange;
    }

    public get asAt(): OffsetDateTime {
        return this.quote.asAt;
    }

    public get bid(): number | null {
        return this.quote.bid;
    }

    public get bidVolume(): number {
        return this.quote.bidVolume;
    }

    public get ask(): number | null {
        return this.quote.ask;
    }

    public get askVolume(): number {
        return this.quote.askVolume;
    }

    public get mid(): number {
        return this.quote.mid;
    }

    public get open(): number {
        return this.quote.open;
    }

    public get high(): number {
        return this.quote.high;
    }

    public get low(): number {
        return this.quote.low;
    }

    public get last(): number | null {
        return this.quote.last;
    }

    public get matchPrice(): number | null {
        return this.quote.matchPrice;
    }

    public get matchVolume(): number {
        return this.quote.matchVolume;
    }

    public get prevClose(): number {
        return this.quote.prevClose;
    }

    public get basisOfQuotation(): BasisOfQuotations {
        return this.quote.basisOfQuotation;
    }

    public get change(): number {
        return this.quote.change;
    }

    public get changePct(): number {
        return this.quote.changePct;
    }

    public get tradeCount(): number {
        return this.quote.tradeCount;
    }

    public get totalTradedValue(): number {
        return this.quote.totalTradedValue;
    }

    public get totalTradedVolume(): number {
        return this.quote.totalTradedVolume;
    }

    public get priceBasis(): PriceBasis {
        return this.quote.priceBasis;
    }

    public get quoteStatus(): QuoteStatus {
        return this.quote.quoteStatus;
    }

    public get tradingState(): TradingState {
        return this.quote.tradingState;
    }

    public get portfolioType(): PortfolioType {
        return this.quote.portfolioType;
    }

    public get changeDirection(): QuoteChangeDirection {
        if ((this.previousQuote == null) || (this.quote.last === this.previousQuote.last)) {
            return QuoteChangeDirection.NO_CHANGE;
        } else if ((this.quote.last == null) || (this.previousQuote.last == null)) {
            return QuoteChangeDirection.NO_CHANGE;
        } else if (this.quote.last > this.previousQuote.last) {
            return QuoteChangeDirection.UP;
        } else {
            return QuoteChangeDirection.DOWN;
        }
    }

    public get isValid(): boolean {
        return this.quote.quoteStatus === QuoteStatus.OK;
    }

    public get isInvalid(): boolean {
        return !this.isValid;
    }

    public get isOpen(): boolean {
        return isMarketOpenState(this.quote.tradingState);
    }
}

export class UnderlyingQuoteViewModel extends QuoteViewModel<UnderlyingQuoteRecord> {

    public constructor(quote: UnderlyingQuoteRecord,
                       previousQuote: UnderlyingQuoteRecord | null,
                       strategy: PriceStrategy) {
        super(quote, previousQuote, strategy);
    }

    public clone(): QuoteViewModel<UnderlyingQuoteRecord> {
        return new UnderlyingQuoteViewModel(this.quote, this.previousQuote, this.strategy);
    }
}

export class WarrantQuoteViewModel extends QuoteViewModel<WarrantQuoteRecord> {

    public constructor(quote: WarrantQuoteRecord,
                       previousQuote: WarrantQuoteRecord | null,
                       strategy: PriceStrategy) {
        super(quote, previousQuote, strategy);
    }

    public clone(): QuoteViewModel<WarrantQuoteRecord> {
        return new WarrantQuoteViewModel(this.quote, this.previousQuote, this.strategy);
    }
}

export class OptionQuoteViewModel extends QuoteViewModel<OptionQuoteRecord> {

    public constructor(quote: OptionQuoteRecord,
                       previousQuote: OptionQuoteRecord | null,
                       strategy: PriceStrategy) {
        super(quote, previousQuote, strategy);
    }

    public clone(): QuoteViewModel<OptionQuoteRecord> {
        return new OptionQuoteViewModel(this.quote, this.previousQuote, this.strategy);
    }
}

export class TmcQuoteViewModel extends QuoteViewModel<TmcQuoteRecord> {

    public readonly opposite: boolean;

    public constructor(quote: TmcQuoteRecord,
                       previousQuote: TmcQuoteRecord | null,
                       public readonly legs: ReadonlyArray<QuoteViewModel>,
                       strategy: PriceStrategy) {
        super(quote, previousQuote, strategy);
        this.opposite = quote.opposite;
    }

    public clone(): QuoteViewModel<TmcQuoteRecord> {
        const legClones = this.legs.map((l) => l.clone());
        return new TmcQuoteViewModel(this.quote, this.previousQuote, legClones, this.strategy);
    }
}
